import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mic, MailWarning, Loader2, FileAudio, ArrowRight, Sparkles, Crown, CalendarHeart, ShoppingBag } from "lucide-react";
export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [scans, setScans] = useState(null);
  const [resending, setResending] = useState(false);
  const [membership, setMembership] = useState(null);
  const [calendly, setCalendly] = useState("");

  useEffect(() => {
    api.get("/scans").then((r) => setScans(r.data)).catch(() => setScans([]));
    api.get("/membership").then((r) => setMembership(r.data)).catch(() => setMembership({ active: false }));
    api.get("/settings/public").then((r) => setCalendly(r.data.calendly_link || "")).catch(() => {});
  }, []);

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent. Check your inbox.");
    } catch {
      toast.error("Could not send email. Try again shortly.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Dashboard</p>
        <h1 className="font-serif text-4xl tracking-tight mb-8">Hello, {user?.name?.split(" ")[0]}</h1>

        <div className="rounded-2xl border border-border bg-card p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="membership-status">
          <div className="flex items-center gap-4">
            <span className="grid place-items-center h-12 w-12 rounded-full bg-primary/15 text-primary shrink-0"><Crown strokeWidth={1.5} /></span>
            <div>
              {membership?.active ? (
                <>
                  <p className="font-medium">Active membership · {membership.plan === "membership_30" ? "30 days" : membership.plan === "membership_60" ? "60 days" : "90 days"}</p>
                  <p className="text-xs text-muted-foreground">Renews / expires {membership.expires_at ? new Date(membership.expires_at).toLocaleDateString() : "—"}</p>
                </>
              ) : (
                <>
                  <p className="font-medium">Free plan</p>
                  <p className="text-xs text-muted-foreground">Upgrade to unlock all reports, prescriptions and the meditation library.</p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {calendly && <a href={calendly} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="rounded-full border-border" data-testid="book-1on1-btn"><CalendarHeart size={16} strokeWidth={1.5} /> Book a 1:1 session</Button></a>}
            <Link to="/app/purchases"><Button variant="outline" className="rounded-full border-border" data-testid="my-purchases-btn"><ShoppingBag size={16} strokeWidth={1.5} /> My purchases</Button></Link>
            {!membership?.active && <Link to="/products/health-memberships"><Button className="rounded-full bg-primary text-primary-foreground font-semibold" data-testid="dashboard-upgrade-btn">Upgrade</Button></Link>}
          </div>
        </div>

        {!user?.email_verified && (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="verify-banner">
            <div className="flex items-start gap-3">
              <MailWarning className="text-primary shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-secondary-foreground">Verify your email to unlock scanning. Check your inbox for the link.</p>
            </div>
            <Button onClick={resend} disabled={resending} variant="outline" className="rounded-full border-border whitespace-nowrap" data-testid="resend-verification-btn">
              {resending && <Loader2 size={16} className="animate-spin" />} Resend email
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Link to="/app/scan" className="md:col-span-2 group rounded-2xl border border-border bg-card p-8 hover:border-primary/40 transition-colors" data-testid="new-scan-card">
            <span className="grid place-items-center h-12 w-12 rounded-full bg-primary/15 text-primary mb-5"><Mic strokeWidth={1.5} /></span>
            <h2 className="font-serif text-2xl mb-2">Take a new voice scan</h2>
            <p className="text-muted-foreground text-sm max-w-sm mb-5">30 seconds of steady voice generates your free Physical Health Mapping across octaves 4–6.</p>
            <span className="inline-flex items-center gap-2 text-primary text-sm font-semibold">Start scan <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" strokeWidth={1.5} /></span>
          </Link>
          <div className="rounded-2xl border border-border bg-card p-8 flex flex-col justify-between">
            <div>
              <span className="grid place-items-center h-12 w-12 rounded-full bg-accent/20 text-accent-foreground mb-5"><Sparkles strokeWidth={1.5} /></span>
              <h3 className="font-serif text-xl mb-2">Unlock all reports</h3>
              <p className="text-muted-foreground text-sm">Emotional, Blood Biomarkers, sound-frequency prescriptions and the 11-day cycle come with membership.</p>
            </div>
            <Link to="/products" className="mt-5"><Button variant="outline" className="w-full rounded-full border-border" data-testid="view-plans-btn">View plans</Button></Link>
          </div>
        </div>

        <Link to="/app/astro" className="group flex items-center justify-between rounded-2xl border border-primary/25 bg-gradient-to-r from-primary/10 to-transparent p-6 mb-12 hover:border-primary/50 transition-colors" data-testid="astro-scan-card">
          <div className="flex items-center gap-4">
            <span className="grid place-items-center h-12 w-12 rounded-full bg-primary/15 text-primary shrink-0"><Sparkles strokeWidth={1.5} /></span>
            <div>
              <h2 className="font-serif text-xl">Nakshatra Mitra · Astro Frequency Scan</h2>
              <p className="text-muted-foreground text-sm">See your 9 planets & 27 Nakshatras as a live chart dashboard.</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-primary group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
        </Link>

        <h2 className="font-serif text-2xl mb-5">Your scan history</h2>
        {scans === null ? (
          <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>
        ) : scans.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="history-empty">
            <FileAudio className="mx-auto mb-4 text-muted-foreground/60" strokeWidth={1.5} />
            No scans yet. Take your first scan to see your frequency map here.
          </div>
        ) : (
          <div className="space-y-3" data-testid="history-list">
            {scans.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors" data-testid={`history-item-${s.id}`}>
                <Link to={`/app/scan?scan=${s.id}`} className="flex items-center gap-4 flex-1 min-w-0 group">
                  <span className="grid place-items-center h-10 w-10 rounded-lg bg-secondary text-primary shrink-0"><FileAudio size={18} strokeWidth={1.5} /></span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">Voice scan · {s.duration}s</p>
                    <p className="text-xs text-muted-foreground truncate">{new Date(s.created_at).toLocaleString()}</p>
                  </div>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/app/scan?scan=${s.id}`} data-testid={`history-health-${s.id}`}
                    className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors">
                    Health <ArrowRight size={13} strokeWidth={1.5} />
                  </Link>
                  <Link to={`/app/astro?scan=${s.id}`} data-testid={`history-astro-${s.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/30 px-3 py-1.5 text-xs text-primary font-medium hover:bg-primary/20 transition-colors">
                    <Sparkles size={13} strokeWidth={1.5} /> Astro
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}